'use client';

import {
  computeLinkExpiry,
  ExpiryClock,
  LinkSentSignInResend,
  LoginDisplayError,
  NonSSRWrapper,
  PasswordlessLogin as PasswordlessComponent,
  SignInMethodUsed,
  SignInState,
} from '@verifime/components';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocalUserCache, usePasswordless } from './hooks/usePasswordless';
import type { RecoveryReason } from '../model';

// Hoisted to a stable module-level reference. `loginError` is not itself an
// effect dependency, so a fresh `new Error(...)` on every run did not by
// itself drive the render loop (that came from expiredLinkCallback's
// identity changing after router.push() re-rendered the parent; see the
// useRef guard below). But minting a new object here was still a pointless
// state write and re-render on every pass. Reusing one instance makes that
// state update a no-op once the error is already showing.
// Tagged as LoginDisplayError so ErrorMessage renders this specific string
// instead of its generic fallback; see LoginAlert.tsx in @verifime/components.
const SIGN_IN_LINK_ERROR = new LoginDisplayError('Unable to sign-in with this link');

export default function PasswordlessLogin({
  children,
  signInCallback,
  getMegicLinkRedirectUri,
  onSignInAsDifferentUser,
  expiredLinkCallback,
  initialEmail,
  extraSlot,
  notice,
  magicLinkPending,
  signInLinkRequested,
}: Readonly<{
  children: React.ReactNode;
  signInCallback: () => void;
  getMegicLinkRedirectUri?: () => Promise<string>;
  onSignInAsDifferentUser?: () => void;
  expiredLinkCallback?: (reason?: RecoveryReason) => void;
  initialEmail?: string;
  extraSlot?: React.ReactNode;
  notice?: React.ReactNode;
  /**
   * Copy and slots for the deferred magic-link confirmation screen
   * (MagicLinkContinue). Only reachable when the page has opted PasswordlessContextProvider
   * into deferred consumption (its deferConsumption prop); harmless to omit
   * otherwise, since this container never reaches MAGIC_LINK_PENDING without
   * that opt-in. `onContinue` and `expiry` are not part of this prop: this
   * container supplies onContinue itself (wired to usePasswordless()'s
   * confirmSignInWithLink) and builds expiry from `expiryLabel` plus the
   * hook's own pendingSignInLinkExpiresAt, because neither is copy the page
   * authors directly.
   */
  magicLinkPending?: {
    title: React.ReactNode;
    actionLabel: React.ReactNode;
    attribution?: React.ReactNode;
    requirements?: React.ReactNode;
    /** The desktop complement to `requirements`, opposed to it in visibility rather than layered
     *  alongside it: @verifime/components' MagicLinkContinue renders at most one of the two at
     *  any one width. Typically an IdentityRequirementSummary built from the documents groups
     *  plus a webcam requirement group, followed by a DesktopQrReveal. */
    desktopRequirements?: React.ReactNode;
    /** Label rendered above the countdown by ExpiryClock. Still optional (a caller may withhold
     *  it, or the hook may not have an expiry to pair it with - see the `expiry` gate below):
     *  D4 originally deferred this pending whether the value's source needed a
     *  VPW backend change. Task 11 traced it and confirmed the source is client-side and already
     *  correct: `magic-link.ts`'s `checkCurrentLocationForSignInLink` reads the fragment's own
     *  `exp` claim, `signInWithLink` compares it against `Date.now()` to decide whether the link
     *  itself has expired, and on a still-valid deferred link passes that same `exp` (as
     *  `new Date(exp * 1000)`) through to `pendingSignInLinkExpiresAt` below - i.e. `exp` denotes
     *  the sign-in link's own expiry, not a session's, matching what this label says. No backend
     *  change is needed; slot 06's string was approved 2026-09-21. */
    expiryLabel?: React.ReactNode;
    errorMessage?: React.ReactNode;
  };
  /**
   * Copy for the resend affordance on the SIGNIN_LINK_REQUESTED screen (Task 5, "Link sent, one
   * screen", settled 2026-09-17). Omitted, that screen renders exactly as it always has - a
   * static status message with no address, no expiry and no resend affordance - so an existing
   * caller (e.g. the isInertMagicLinkEnabled toggle off) sees no change.
   *
   * Every field here is copy this container forwards verbatim into a LinkSentSignInResend it
   * composes itself (see the `signInLinkRequested` prop built below) - `onResend`, `isResending`
   * and `error` are NOT part of this prop, because none of the three is copy the page authors
   * directly: this container builds `onResend` itself (it re-runs `performSignIn` with the SAME
   * email the original request used - see `requestedSignInEmailRef` below, not `initialEmail` -
   * and the fresh code the customer just typed), tracks `isResending` from that call's own
   * lifetime, and tracks `error` from whether that same call was refused.
   */
  signInLinkRequested?: {
    /** Slot 16, e.g. "Didn't get it?". Forwarded to LinkSentSignInResend's own `leadLine`. */
    leadLine: React.ReactNode;
    /** Slot 18, e.g. "Resend the email". Forwarded to LinkSentSignInResend's own
     *  `resendActionLabel`. */
    resendActionLabel: React.ReactNode;
    /** Slot 31, e.g. "Please try again later, we will let you know.". Forwarded to
     *  LinkSentSignInResend's own `pressedInsideWindowMessage`. The caller supplies the whole
     *  node directly; there is no number involved. */
    pressedInsideWindowMessage: React.ReactNode;
    /** Slot 28. Forwarded to LinkSentSignInResend's own `replacementWarning`. */
    replacementWarning: React.ReactNode;
    /** Slot 34. Forwarded to LinkSentSignInResend's own `resendNowMessage`. */
    resendNowMessage: React.ReactNode;
    /** Slot 19, e.g. "Enter the code from your authenticator app.". Forwarded to
     *  LinkSentSignInResend's own `codePrompt`. */
    codePrompt: React.ReactNode;
    /** Slot 20, e.g. "Resend". Forwarded to LinkSentSignInResend's own `resendButtonLabel`. */
    resendButtonLabel: React.ReactNode;
    /** Slot 32, e.g. "Code not accepted.". Forwarded to LinkSentSignInResend's own
     *  `codeRejectedMessage`, shown only while this container's own `error` is true. */
    codeRejectedMessage: React.ReactNode;
    throttleSeconds?: number;
  };
}>) {
  const [signInState, setSignInState] = useState<SignInState>(SignInState.NOT_SIGNED_IN);
  const [loginError, setLoginError] = useState<Error | undefined>(undefined);
  const {
    requestSignInLink,
    authenticateWithFido2,
    signInStatus,
    signingInStatus,
    tokens,
    lastError,
    signInLinkFailureReason,
    pendingSignInLinkExpiresAt,
    confirmSignInWithLink,
  } = usePasswordless();
  const { lastSignedInUsers } = useLocalUserCache();
  const { email, credentials } = lastSignedInUsers?.at(0) ?? {};
  // The caller (e.g. index.tsx's expiredLinkRedirect) typically recreates
  // expiredLinkCallback on every render, which is in this effect's dep array.
  // Without this guard, each rerender would re-invoke the callback (e.g.
  // pushing to the router again). It should fire at most once per mount.
  const expiredLinkCallbackFiredRef = useRef(false);
  // Set the moment a press on the MAGIC_LINK_PENDING screen calls
  // confirmSignInWithLink, cleared once the resulting terminal status (success
  // or failure) is handled below. Distinguishes "this classified failure is the
  // direct result of a press on this screen" from every other route to the same
  // SIGNIN_LINK_EXPIRED/INVALID_SIGNIN_LINK statuses (an automatic, non-deferred
  // consumption failure, or the link expiring while the customer just sits on
  // the screen without pressing anything) - only the former should keep the
  // customer on this screen instead of bouncing to the generic banner.
  const confirmAttemptInFlightRef = useRef(false);
  // Whether the pending-confirmation screen should currently show the caller's
  // errorMessage: true only immediately after a failed press, cleared as soon
  // as another press is made. Never holds a message itself - the copy stays a
  // prop the page supplies (magicLinkPending.errorMessage), exactly like title
  // and actionLabel; this is purely a "should I show it right now" flag.
  const [magicLinkPendingPressFailed, setMagicLinkPendingPressFailed] = useState(false);
  // The email a MAGIC_LINK performSignIn call was actually made with, captured at that call site
  // (see performSignIn below) - not `initialEmail` and not `lastSignedInUsers`' cached email,
  // which can both be stale or simply absent for a customer signing in on this device for the
  // first time. A resend on SIGNIN_LINK_REQUESTED reuses this SAME email, because that is the
  // account the customer is actually trying to reach, exactly as the original request did.
  //
  // Deliberately NOT storing the one-time password alongside it. See
  // SignInLinkRequested.tsx's own doc comment on its `freshCode` field for the full reasoning: a
  // one-time code is a live possession check, and this container only ever receives one as a
  // call-time argument to performSignIn/handleResendSignInLink, passed straight through to
  // requestSignInLink and never assigned to a ref, a piece of state, or anything else that would
  // outlive that one call.
  const requestedSignInEmailRef = useRef<string | undefined>(undefined);
  // The moment the last successful MAGIC_LINK send happened - the very first request as well as
  // every resend that genuinely sends - set once, inside performSignIn's own success branch,
  // never read from "now" at render time. LinkSent's `expiresAt` (built into the
  // `signInLinkRequested` prop below) is derived from this via computeLinkExpiry, mirroring
  // useGoogleSsoSignUp's own `sentAt` and the `expiresAt` GoogleSsoSignUpView derives from it: a
  // re-render with no fresh send always shows the SAME expiry rather than one that creeps forward
  // with the clock.
  const [sentAt, setSentAt] = useState<Date | null>(null);
  const [isResendingSignInLink, setIsResendingSignInLink] = useState(false);
  // Whether the fresh code most recently submitted on SIGNIN_LINK_REQUESTED was refused: true
  // only immediately after a failed resend, cleared as soon as another resend is pressed. Mirrors
  // magicLinkPendingPressFailed above - same shape, same reason. Forwarded straight to
  // LinkSentSignInResend's own `error` (a boolean, not a message): the rejection copy itself is
  // now a fixed part of the page's own signInLinkRequested.codeRejectedMessage (slot 32), not a
  // caller-supplied string this container gates - see the `signInLinkRequested` prop's own doc
  // comment above.
  const [resendSignInLinkFailed, setResendSignInLinkFailed] = useState(false);
  // Local ownership of the SIGNIN_LINK_REQUESTED screen's identity - mirrors
  // useGoogleSsoSignUp's own local `display` state. Previously this screen's identity was
  // *derived* from signingInStatus/signInStatus on every render, which meant a resend press
  // synchronously
  // flipping signingInStatus through REQUESTING_SIGNIN_LINK/SIGNIN_LINK_REQUEST_FAILED - neither
  // handled by the effect's other branches - fell through to the catch-all NOT_SIGNED_IN and
  // evicted the customer. Tracking "am I on this screen" as its own boolean, set explicitly only
  // at genuine enter/exit moments, removes that derivation: once true, signingInStatus/
  // signInStatus become inputs this screen reads (for the resend's isResending/error props)
  // rather than a command that can navigate it away.
  const [isOnSignInLinkRequestedScreen, setIsOnSignInLinkRequestedScreen] = useState(false);

  useEffect(() => {
    setLoginError(lastError);

    if (isOnSignInLinkRequestedScreen) {
      // Own this screen: leave only via the two explicit exits below, or the explicit exclude-
      // list further down for statuses that have their own meaningful handling elsewhere in this
      // effect. Every other status - including every one a resend attempt can actually produce
      // (REQUESTING_SIGNIN_LINK, SIGNIN_LINK_REQUESTED, SIGNIN_LINK_REQUEST_FAILED) - must not
      // evict it.
      //
      // SIGNING_OUT is checked BEFORE SIGNED_IN+tokens, matching every other branch in this
      // effect (see below): usePasswordless.tsx's signOut() fires statusCb (signingInStatus ->
      // 'SIGNING_OUT') and a separate tokensRemovedLocallyCb (clears tokens) as two independent
      // callbacks. signInStatus's own useMemo checks tokensParsed's expiry BEFORE it ever checks
      // signingInStatus in its ternary chain, so a still-unexpired token wins: signInStatus can
      // briefly still read 'SIGNED_IN' even after signingInStatus has already flipped to
      // 'SIGNING_OUT'. Checking SIGNED_IN first would misread a sign-out during that window as a
      // successful sign-in.
      if (signingInStatus === 'SIGNING_OUT') {
        setIsOnSignInLinkRequestedScreen(false);
        // Local-only resets, distinct from the hook's own lastError: without them, a stale
        // rejection or in-flight flag from a resend attempt on this screen could leak into a
        // later, unrelated login attempt if the customer reaches LoginMethodSelection next
        // without a fresh performSignIn call.
        setLoginError(undefined);
        setResendSignInLinkFailed(false);
        setIsResendingSignInLink(false);
        setSignInState(SignInState.NOT_SIGNED_IN);
        return;
      }
      if (signInStatus === 'SIGNED_IN' && tokens) {
        // Guarded so this fires exactly once even though setIsOnSignInLinkRequestedScreen(false)
        // below is itself an effect dependency: that state flip triggers a second run of this
        // effect, which would otherwise fall through to the pre-existing SIGNED_IN+tokens branch
        // further down and call signInCallback a second time. Mirrors the
        // expiredLinkCallbackFiredRef pattern above - "already handled this transition, don't
        // repeat the side effect" - but keyed off signInState itself rather than a ref, since
        // signInState legitimately resets to NOT_SIGNED_IN on a later, independent sign-out and
        // should be allowed to fire again for a later, independent sign-in in the same mount.
        if (signInState !== SignInState.SIGNED_IN) {
          signInCallback?.();
        }
        setIsOnSignInLinkRequestedScreen(false);
        setSignInState(SignInState.SIGNED_IN);
        return;
      }
      if (
        // Every entry here reads signingInStatus, scoped to the sign-in attempt
        // in progress, and each has its own explicit, meaningful handling
        // further down in this effect (MAGIC_LINK_PENDING, VALIDATE_SIGN_IN_STATUS,
        // the expired/invalid-link banner + expiredLinkCallback, FIDO2) - give up
        // screen ownership and fall through to it, rather than silently absorbing
        // it here the same way every OTHER status (a resend in flight, failed,
        // requested again, or simply no new status at all) legitimately is.
        //
        // Deliberately excludes signInStatus (CHECKING, REFRESHING_SIGN_IN, etc):
        // that status is process-global, describing the browser's stored session
        // rather than this attempt, and can move on its own - e.g. a completely
        // unrelated dead session's background refresh retry in the same tab -
        // which must never take this screen away from the customer. See the
        // login entry screen's own spinner check below, which explains the
        // equivalent omission there.
        signingInStatus === 'SIGNIN_LINK_PENDING_CONFIRMATION' ||
        signingInStatus === 'SIGNING_IN_WITH_LINK' ||
        signingInStatus === 'SIGNIN_LINK_EXPIRED' ||
        signingInStatus === 'INVALID_SIGNIN_LINK' ||
        signingInStatus === 'STARTING_SIGN_IN_WITH_FIDO2' ||
        signingInStatus === 'COMPLETING_SIGN_IN_WITH_FIDO2'
      ) {
        setIsOnSignInLinkRequestedScreen(false);
      } else {
        setSignInState(SignInState.SIGNIN_LINK_REQUESTED);
        return;
      }
    }

    if (signingInStatus === 'SIGNIN_LINK_REQUESTED') {
      // Genuine first-time entry (a fresh request, not a resend - a resend flips local ownership
      // on directly, in performSignIn's success branch below).
      setIsOnSignInLinkRequestedScreen(true);
      setSignInState(SignInState.SIGNIN_LINK_REQUESTED);
      return;
    }

    if (signingInStatus === 'SIGNIN_LINK_PENDING_CONFIRMATION') {
      setSignInState(SignInState.MAGIC_LINK_PENDING);
      return;
    }

    // signInStatus is process-global: REFRESHING_SIGN_IN can reflect a
    // completely unrelated dead session's background refresh retry in this
    // tab, not just a session this customer is part way through. It is
    // reachable only when tokensParsed exists and has already lapsed (the
    // signInStatus memo's first ternary claims every unexpired case as
    // SIGNED_IN), so the spinner this used to drive was always covering a
    // session that is not currently usable. The three app AuthenticatedContainer
    // copies already deliberately treat this same status as "keep the page
    // mounted" - this makes the login screen agree with them instead of
    // reading the identical status as its opposite. Accepted trade-off: the
    // login screen shows the form, not the spinner, while an unrelated
    // background refresh is in flight. CHECKING is unaffected: it means we
    // don't yet know whether a session exists at all (the initial token
    // retrieval from storage, or checking for a sign-in link fragment), which
    // still warrants the spinner.
    if (signInStatus === 'CHECKING') {
      setSignInState(SignInState.VALIDATE_SIGN_IN_STATUS);
      return;
    }

    if (signingInStatus === 'SIGNING_IN_WITH_LINK') {
      // Design board screen 1b, "Signing you in ...", is this press's own waiting screen, and the
      // press is what distinguishes it. confirmAttemptInFlightRef is set synchronously in
      // handleContinueMagicLinkSignIn before confirmSignInWithLink() moves the status here, and it
      // is set true nowhere else, so it reads exactly as "a customer looking at MAGIC_LINK_PENDING
      // chose to spend this link". It is cleared again by both terminal branches below, and the
      // expired/invalid one already reads it exactly this way.
      //
      // Without the ref this branch would also repaint the auto-consume path the inert-magic-link
      // toggle replaces, where signInWithLink spends the link on mount. With the toggle off,
      // MAGIC_LINK_PENDING never renders, nothing ever calls that handler, the ref stays false, and
      // this status keeps mapping to VALIDATE_SIGN_IN_STATUS byte for byte. That is how this change
      // stays inside the toggle without this package ever reading one, which it must not do.
      setSignInState(
        confirmAttemptInFlightRef.current
          ? SignInState.SIGNING_YOU_IN
          : SignInState.VALIDATE_SIGN_IN_STATUS,
      );
      return;
    }

    if (signingInStatus === 'SIGNING_OUT') {
      setSignInState(SignInState.NOT_SIGNED_IN);
      return;
    }

    if (signInStatus === 'SIGNED_IN' && tokens) {
      // Guarded the same way, and for the same reason, as the isOnSignInLinkRequestedScreen
      // block's own copy of this check above: falling through from that block (screen ownership
      // just turned off) re-runs this effect with signInStatus/tokens unchanged, and without this
      // guard this branch would call signInCallback a second time for the same sign-in.
      if (signInState !== SignInState.SIGNED_IN) {
        signInCallback?.();
      }
      // Mirrors the SIGNIN_LINK_EXPIRED/INVALID_SIGNIN_LINK branch's own reset below: a successful
      // sign-in is just as terminal for this press as a failure is, and confirmAttemptInFlightRef
      // must not survive it. Without this, a customer who somehow saw SIGNING_IN_WITH_LINK
      // reported again with no new press (e.g. an unrelated background retry) would find the ref
      // still true and get repainted onto SIGNING_YOU_IN, even though nothing they did caused it.
      confirmAttemptInFlightRef.current = false;
      setSignInState(SignInState.SIGNED_IN);
      return;
    }

    // A used/expired link carries no reason (decision D1); an otherwise-invalid
    // link (broken, server error, etc) carries whatever the hook classified it
    // as. For every route to this status EXCEPT a just-failed press on the
    // MAGIC_LINK_PENDING screen, both are terminal, unrecoverable states for
    // this login attempt, and both must still surface the same banner even
    // where the caller (e.g. client-portal, admin-portal) supplies no
    // expiredLinkCallback at all.
    if (signingInStatus === 'SIGNIN_LINK_EXPIRED' || signingInStatus === 'INVALID_SIGNIN_LINK') {
      if (confirmAttemptInFlightRef.current) {
        // The customer pressed continue on this screen and it failed. Task 1
        // deliberately does not gate a retry behind the dead-fragment set, so
        // stay right here rather than bouncing to the generic banner: the
        // customer can press continue again, and confirmSignInWithLink remains
        // callable exactly as it was before this press (nothing here touches
        // it). expiredLinkCallback is NOT fired: that callback means "this
        // login attempt is over", which is not true while a retry is still on
        // offer.
        confirmAttemptInFlightRef.current = false;
        setSignInState(SignInState.MAGIC_LINK_PENDING);
        setMagicLinkPendingPressFailed(true);
        return;
      }
      if (!expiredLinkCallbackFiredRef.current) {
        expiredLinkCallbackFiredRef.current = true;
        expiredLinkCallback?.(signInLinkFailureReason);
      }
      setSignInState(SignInState.NOT_SIGNED_IN);
      setLoginError(SIGN_IN_LINK_ERROR);
      return;
    }

    if (
      signingInStatus === 'STARTING_SIGN_IN_WITH_FIDO2' ||
      signingInStatus === 'COMPLETING_SIGN_IN_WITH_FIDO2'
    ) {
      setSignInState(SignInState.VALIDATE_SIGN_IN_STATUS);
      return;
    }
    setSignInState(SignInState.NOT_SIGNED_IN);
  }, [
    signInStatus,
    signingInStatus,
    tokens,
    lastSignedInUsers,
    lastError,
    signInCallback,
    expiredLinkCallback,
    signInLinkFailureReason,
    isOnSignInLinkRequestedScreen,
    signInState,
  ]);

  const performSignIn = async (
    email: string,
    signInMethod: SignInMethodUsed,
    oneTimePassword: string = '',
    // Additional, call-site-scoped failure notification, invoked alongside (never instead of)
    // setLoginError below. Optional and additive so every existing caller (e.g. MainLogin, which
    // relies solely on the shared loginError/errorToShow state) is unaffected: this is how
    // handleResendSignInLink learns "this specific attempt failed" without performSignIn's
    // promise ever rejecting, which stays true for every caller exactly as before.
    onError?: (err: Error) => void,
  ) => {
    const magicLinkRedirectUri = await getMegicLinkRedirectUri?.();
    return new Promise<void>((resolve) => {
      if (signInMethod === SignInMethodUsed.MAGIC_LINK && typeof requestSignInLink === 'function') {
        requestedSignInEmailRef.current = email;
        requestSignInLink({
          username: email,
          oneTimePassword,
          redirectUri: magicLinkRedirectUri,
        })
          .signInLinkRequested.then(() => {
            resolve();
            // A link genuinely just went out - the first request as well as a resend that
            // actually sends (a rejected resend never reaches this branch: requestSignInLink's
            // own promise rejects instead, handled in the .catch below). See sentAt's own doc
            // comment above for why this is set here rather than derived from "now" at render
            // time.
            setSentAt(new Date());
            // Cleared here too, not just at the top of handleResendSignInLink below: this success
            // branch is shared by the very first request as well as every resend, and a FIRST
            // request needs this clear as much as a resend does. Without it, a resend that failed
            // in an earlier, already-exited session (see the SIGNING_OUT exit above, which resets
            // this same flag) could otherwise still read stale if that reset were ever bypassed -
            // this is the second, independent place the same staleness is closed off.
            setResendSignInLinkFailed(false);
            // Flip local screen ownership on directly, here, rather than relying on the effect
            // above to derive it from signingInStatus. Previously this used a setTimeout(..., 0)
            // to win a race against the effect's own (now-removed) derivation of signInState from
            // signingInStatus/signInStatus; with local ownership, signingInStatus is never a
            // command that can navigate this screen away once entered, so that race no longer
            // exists and the timeout is no longer needed.
            setIsOnSignInLinkRequestedScreen(true);
            setSignInState(SignInState.SIGNIN_LINK_REQUESTED);
          })
          .catch((err) => {
            setLoginError(err);
            onError?.(err);
            console.warn(err);
            resolve();
          });
      } else if (
        signInMethod === SignInMethodUsed.FIDO &&
        typeof authenticateWithFido2 === 'function'
      ) {
        authenticateWithFido2({ username: email, credentials })
          .then(() => {
            resolve();
            setTimeout(() => {
              setSignInState(SignInState.VALIDATE_SIGN_IN_STATUS);
            }, 0);
          })
          .catch((err) => {
            setLoginError(err);
            onError?.(err);
            console.warn(err);
            resolve();
          });
      }
    });
  };

  const handleContinueMagicLinkSignIn = () => {
    // Cleared on every press, including a retry: a fresh attempt should not
    // keep showing the previous attempt's failure while it is in flight.
    setMagicLinkPendingPressFailed(false);
    confirmAttemptInFlightRef.current = true;
    confirmSignInWithLink?.();
  };

  // Reached only from SignInLinkRequested's resend action, which itself only renders once the
  // caller opted in via signInLinkRequested - the guard below is a defensive no-op for a state
  // that should not be reachable, not a path this container expects to take.
  const handleResendSignInLink = (freshCode: string) => {
    const attemptedEmail = requestedSignInEmailRef.current;
    if (!attemptedEmail) {
      return;
    }
    // Cleared on every press, including a retry: a fresh attempt should not keep showing the
    // previous attempt's failure while it is in flight - same reasoning as
    // handleContinueMagicLinkSignIn's setMagicLinkPendingPressFailed(false) above.
    setResendSignInLinkFailed(false);
    setIsResendingSignInLink(true);
    performSignIn(attemptedEmail, SignInMethodUsed.MAGIC_LINK, freshCode, () =>
      setResendSignInLinkFailed(true),
    ).finally(() => {
      setIsResendingSignInLink(false);
    });
  };

  // Memoised on sentAt alone: while sentAt is still null (no MAGIC_LINK send has happened yet)
  // this evaluates once at mount and is never displayed - SIGNIN_LINK_REQUESTED is never reached
  // without a successful send first. Once a send happens it recomputes only when sentAt itself
  // changes, never on an unrelated re-render (isResendingSignInLink toggling, etc.) - mirrors
  // GoogleSsoSignUpView.tsx's own expiresAt memo exactly.
  const signInLinkExpiresAt = useMemo(() => computeLinkExpiry(sentAt ?? new Date()), [sentAt]);

  return (
    <NonSSRWrapper>
      <PasswordlessComponent
        performSignIn={performSignIn}
        signInState={signInState}
        lastSignedInEmail={initialEmail ?? email ?? ''}
        errorToShow={loginError}
        onSignInAsDifferentUser={onSignInAsDifferentUser}
        extraSlot={extraSlot}
        notice={notice}
        magicLinkPending={
          magicLinkPending && {
            title: magicLinkPending.title,
            actionLabel: magicLinkPending.actionLabel,
            onContinue: handleContinueMagicLinkSignIn,
            attribution: magicLinkPending.attribution,
            requirements: magicLinkPending.requirements,
            desktopRequirements: magicLinkPending.desktopRequirements,
            // Omitted whenever the hook has not reported an expiry (ExpiryClock requires a real
            // Date, e.g. no fragment was present at all) or the caller withheld expiryLabel: both
            // customer-portal and client-portal now supply it once the toggle is on (Task 11),
            // but the type keeps this optional since either input can still genuinely be absent.
            expiry:
              magicLinkPending.expiryLabel && pendingSignInLinkExpiresAt ? (
                <ExpiryClock label={magicLinkPending.expiryLabel} expiresAt={pendingSignInLinkExpiresAt} />
              ) : undefined,
            // Only shown right after a failed press (see the
            // SIGNIN_LINK_EXPIRED/INVALID_SIGNIN_LINK branch above); otherwise
            // this stays undefined even when the page supplied a message, so
            // the slot is empty on first load and after a successful press.
            // The string itself is never authored or defaulted here - if the
            // page did not supply one, this is undefined too, and the screen
            // simply stays in place without a message.
            errorMessage: magicLinkPendingPressFailed ? magicLinkPending.errorMessage : undefined,
          }
        }
        signInLinkRequested={
          signInLinkRequested && {
            // requestedSignInEmailRef.current is set synchronously, before the async
            // requestSignInLink call (see performSignIn above), and SIGNIN_LINK_REQUESTED can
            // only ever be reached after that has already happened - the `?? ''` fallback exists
            // purely to satisfy email's non-optional type, matching lastSignedInEmail's own
            // `initialEmail ?? email ?? ''` a few lines up, not because it is ever actually
            // displayed empty.
            email: requestedSignInEmailRef.current ?? '',
            expiresAt: signInLinkExpiresAt,
            resend: (
              <LinkSentSignInResend
                leadLine={signInLinkRequested.leadLine}
                resendActionLabel={signInLinkRequested.resendActionLabel}
                pressedInsideWindowMessage={signInLinkRequested.pressedInsideWindowMessage}
                replacementWarning={signInLinkRequested.replacementWarning}
                resendNowMessage={signInLinkRequested.resendNowMessage}
                codePrompt={signInLinkRequested.codePrompt}
                resendButtonLabel={signInLinkRequested.resendButtonLabel}
                codeRejectedMessage={signInLinkRequested.codeRejectedMessage}
                throttleSeconds={signInLinkRequested.throttleSeconds}
                onResend={handleResendSignInLink}
                isResending={isResendingSignInLink}
                // True only immediately after a failed resend (see handleResendSignInLink
                // above), cleared the moment another resend is pressed - exactly like
                // magicLinkPending.errorMessage's own gate a few lines up, except this is a
                // boolean LinkSentSignInResend renders its own fixed copy against, not a message
                // this container threads through.
                error={resendSignInLinkFailed}
              />
            ),
          }
        }
      >
        {children}
      </PasswordlessComponent>
    </NonSSRWrapper>
  );
}
